import { useEffect, type ReactNode } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import SelectFilter from "./pages/select-filter";
import ScenarioPreview from "./pages/scenario-preview";
import Interview from "./pages/interview";
import VisemeDebug from "./pages/viseme-debug";
import Evaluation from "./pages/evaluation";
import ExperimentPostSurvey from "./pages/experiment-post-survey";
import ScoresList from "./pages/ScoresList";
import MyPage from "./pages/mypage";
import StudentHomeMock from "./pages/student-home-mock";
import StudentLearning from "./pages/student-learning";
import StudentTaskBriefing from "./pages/student-task-briefing";
import {
  StudentHome,
  StudentProfile,
  StudentScores,
  StudentSettings,
} from "./pages/student-mobile-pages";
import Login from "./pages/login";
import GuestEntry from "./pages/guest-entry";
import AdminGuestPasses from "./pages/admin/GuestPasses";
import NotFound from "./pages/not-found";
import AudioFiles from "./pages/audio-files";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminUsers from "./pages/admin/Users";
import AdminAvatars from "./pages/admin/Avatars";
import AdminConditions from "./pages/admin/Conditions";
import QuestionnairesList from "./pages/admin/QuestionnairesList";
import InstructionsList from "./pages/admin/InstructionsList";
import InstructionVariantBuilder from "./pages/admin/InstructionVariantBuilder";
import CourseScenarioSetup from "./pages/admin/CourseScenarioSetup";
import AdminLLMs from "./pages/admin/LLMs";
import AdminPrompts from "./pages/admin/Prompts";
import AdminInterviews from "./pages/admin/Interviews";
import AdminScores from "./pages/admin/Scores";
import AdminHistories from "./pages/admin/Histories";
import AdminTtsPronunciationDictionary from "./pages/admin/TtsPronunciationDictionary";
import AdminSttCorrectionDictionary from "./pages/admin/SttCorrectionDictionary";
import AdminSttSettings from "./pages/admin/SttSettings";
import AdminTechnicalTerms from "./pages/admin/TechnicalTerms";
import AdminTtsCache from "./pages/admin/TtsCache";
import TeacherStudents from "./pages/teacher/Students";
import TeacherStudentMyPage from "./pages/teacher/StudentMyPage";
import { TeacherClassDashboard, TeacherClasses } from "./pages/teacher/Classes";
import TeacherRanking, { DemoTeacherRanking, PublicRankingSample } from "./pages/teacher/Ranking";
import PublicStudentScoreSample, { DemoTeacherStudentScores } from "./pages/teacher/PublicStudentScoreSample";
import AdminGuard from "./components/admin/AdminGuard";
import { AuthService } from "./lib/auth";
import { useQuery } from "@tanstack/react-query";
import type { StaffRole, UserProfile } from "./types";
import { GuestDirectBriefingGate } from "./components/GuestDirectBriefingGate";
import { StudentAppHeader } from "./components/student/StudentAppHeader";
import { STUDENT_HEADER_HEIGHT, StudentHeader } from "./components/student/StudentHeader";
import { NavButton, IconHome } from "./components/HeaderAtoms";
import { DemoTeacherDashboard } from "./pages/admin/Dashboard";
import { DemoTeacherScores } from "./pages/admin/Scores";
import { DemoTeacherStudents } from "./pages/teacher/Students";
import { DemoTeacherClassDashboard } from "./pages/teacher/Classes";
import { DEMO_TEACHER_BASE } from "./lib/demo-teacher";

const EDUCATOR_ADMIN_PATH_ALIASES: Record<string, string> = {
  "/admin": "/teacher",
  "/admin/users": "/teacher/students",
  "/admin/guest-passes": "/teacher/guest-passes",
  "/admin/scores": "/teacher/scores",
  "/admin/avatars": "/teacher/avatars",
  "/admin/conditions": "/teacher/conditions",
  "/admin/questionnaires": "/teacher/questionnaires",
  "/admin/instructions": "/teacher/instructions",
  "/admin/course-scenarios": "/teacher/course-scenarios",
  "/admin/instruction-builder": "/teacher/instruction-builder",
  "/admin/tts-pronunciation-dictionary":
    "/teacher/tts-pronunciation-dictionary",
  "/admin/stt-settings": "/teacher/stt-settings",
  "/admin/technical-terms": "/teacher/technical-terms",
};

function educatorPathForAdminPath(path: string): string | null {
  return EDUCATOR_ADMIN_PATH_ALIASES[path] ?? null;
}

function MainRouter() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/g/:token" component={GuestEntry} />
      <Route path="/select-filter" component={SelectFilter} />
      <Route path="/scenario-preview" component={ScenarioPreview} />
      <Route path="/interview" component={Interview} />
      <Route path="/viseme-debug" component={VisemeDebug} />
      <Route path="/evaluation" component={Evaluation} />
      <Route path="/experiment/post-survey" component={ExperimentPostSurvey} />
      <Route path="/scores" component={ScoresList} />
      <Route path="/audio-files" component={AudioFiles} />
      <Route path="/" component={Login} />
      <Route component={NotFound} />
    </Switch>
  );
}

function AdminRouter() {
  return (
    <AdminGuard>
      <WorkspaceAccessRedirect>
        <Switch>
          <Route path="/admin/users" component={AdminUsers} />
          <Route path="/admin/guest-passes" component={AdminGuestPasses} />
          <Route path="/admin/avatars" component={AdminAvatars} />
          <Route path="/admin/conditions" component={AdminConditions} />
          <Route path="/admin/questionnaires" component={QuestionnairesList} />
          <Route path="/admin/instructions" component={InstructionsList} />
          <Route
            path="/admin/course-scenarios"
            component={CourseScenarioSetup}
          />
          <Route
            path="/admin/instruction-builder"
            component={InstructionVariantBuilder}
          />
          <Route path="/admin/llms" component={AdminLLMs} />
          <Route path="/admin/prompts" component={AdminPrompts} />
          <Route path="/admin/interviews" component={AdminInterviews} />
          <Route path="/admin/scores" component={AdminScoresRoute} />
          <Route path="/admin/histories" component={AdminHistories} />
          <Route
            path="/admin/tts-pronunciation-dictionary"
            component={AdminTtsPronunciationDictionary}
          />
          <Route
            path="/admin/stt-correction-dictionary"
            component={AdminSttCorrectionDictionary}
          />
          <Route path="/admin/stt-settings" component={AdminSttSettings} />
          <Route
            path="/admin/technical-terms"
            component={AdminTechnicalTerms}
          />
          <Route
            path="/admin/performance/tts-cache"
            component={AdminTtsCache}
          />
          <Route path="/admin" component={AdminLandingRoute} />
          <Route path="/teacher/guest-passes" component={TeacherGuestPassesRoute} />
          <Route path="/teacher/users" component={TeacherUsersRoute} />
          <Route path="/teacher/avatars" component={TeacherAvatarsRoute} />
          <Route
            path="/teacher/conditions"
            component={TeacherConditionsRoute}
          />
          <Route
            path="/teacher/questionnaires"
            component={TeacherQuestionnairesRoute}
          />
          <Route
            path="/teacher/instructions"
            component={TeacherInstructionsRoute}
          />
          <Route
            path="/teacher/course-scenarios"
            component={TeacherCourseScenarioSetupRoute}
          />
          <Route
            path="/teacher/instruction-builder"
            component={TeacherInstructionVariantBuilderRoute}
          />
          <Route
            path="/teacher/tts-pronunciation-dictionary"
            component={TeacherTtsPronunciationDictionaryRoute}
          />
          <Route
            path="/teacher/stt-settings"
            component={TeacherSttSettingsRoute}
          />
          <Route
            path="/teacher/technical-terms"
            component={TeacherTechnicalTermsRoute}
          />
          <Route path="/teacher/scores" component={TeacherScoresRoute} />
          <Route path="/teacher/rankings" component={TeacherRankingRoute} />
          <Route
            path="/teacher/classes/:classId"
            component={TeacherClassDashboardRoute}
          />
          <Route path="/teacher/classes" component={TeacherClassesRoute} />
          <Route
            path="/teacher/students/:studentId"
            component={TeacherStudentMyPageRoute}
          />
          <Route path="/teacher/students" component={TeacherStudentsRoute} />
          <Route path="/teacher" component={TeacherLandingRoute} />
          <Route path="/mypage/home" component={StudentHomeRoute} />
          <Route path="/mypage/learning" component={StudentLearningRoute} />
          <Route path="/mypage/task-briefing" component={StudentTaskBriefing} />
          <Route path="/mypage/scores" component={StudentScoresRoute} />
          <Route path="/mypage/settings" component={StudentSettingsRoute} />
          <Route path="/mypage/profile" component={StudentProfileRoute} />
          <Route path="/mypage" component={MyPageRoute} />
          <Route component={NotFound} />
        </Switch>
      </WorkspaceAccessRedirect>
    </AdminGuard>
  );
}

function WorkspaceLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-sm font-bold text-gray-500">
        画面を準備しています...
      </div>
    </div>
  );
}

function RedirectTo({ to }: { to: string }) {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation(to);
  }, [setLocation, to]);

  return <WorkspaceLoading />;
}

function useCurrentStaffRole(): {
  role: StaffRole | undefined;
  isLoading: boolean;
} {
  const authService = AuthService.getInstance();
  const { data: currentUser, isLoading } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => authService.getCurrentUser(),
    staleTime: 5 * 60 * 1000,
  });
  return { role: currentUser?.role, isLoading };
}

function WorkspaceAccessRedirect({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { role, isLoading } = useCurrentStaffRole();

  if (isLoading) return <WorkspaceLoading />;
  if (
    role === "student" &&
    (location.startsWith("/admin") || location.startsWith("/teacher"))
  ) {
    return <RedirectTo to="/mypage" />;
  }
  if (role === "educator" && location.startsWith("/admin")) {
    const educatorPath = educatorPathForAdminPath(location);
    if (educatorPath) return <RedirectTo to={educatorPath} />;
  }

  return <>{children}</>;
}

function AdminLandingRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  if (role === "educator") return <RedirectTo to="/teacher" />;
  return <AdminDashboard />;
}

function AdminScoresRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  if (role === "educator") return <RedirectTo to="/teacher/scores" />;
  return <AdminScores />;
}

function TeacherLandingRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminDashboard />;
}

function TeacherStudentsRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role !== "educator")
    return <RedirectTo to={role === "student" ? "/mypage" : "/admin"} />;
  return <TeacherStudents />;
}

function TeacherClassesRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role !== "educator")
    return <RedirectTo to={role === "student" ? "/mypage" : "/admin"} />;
  return <TeacherClasses />;
}

function TeacherRankingRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role !== "educator")
    return <RedirectTo to={role === "student" ? "/mypage" : "/admin"} />;
  return <TeacherRanking />;
}

function TeacherClassDashboardRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role !== "educator")
    return <RedirectTo to={role === "student" ? "/mypage" : "/admin"} />;
  return <TeacherClassDashboard />;
}

function TeacherStudentMyPageRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role !== "educator")
    return <RedirectTo to={role === "student" ? "/mypage" : "/admin"} />;
  return <TeacherStudentMyPage />;
}

function TeacherScoresRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminScores />;
}

function TeacherGuestPassesRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminGuestPasses />;
}

function TeacherUsersRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  if (role === "educator") return <RedirectTo to="/teacher/students" />;
  return <AdminUsers />;
}

function TeacherAvatarsRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminAvatars />;
}

function TeacherConditionsRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminConditions />;
}

function TeacherQuestionnairesRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <QuestionnairesList />;
}

function TeacherInstructionsRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <InstructionsList />;
}

function TeacherCourseScenarioSetupRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <CourseScenarioSetup />;
}

function TeacherInstructionVariantBuilderRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <InstructionVariantBuilder />;
}

function TeacherTtsPronunciationDictionaryRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminTtsPronunciationDictionary />;
}

function TeacherSttSettingsRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminSttSettings />;
}

function TeacherTechnicalTermsRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "student") return <RedirectTo to="/mypage" />;
  return <AdminTechnicalTerms />;
}

function MyPageRoute() {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "educator") return <RedirectTo to="/teacher" />;
  if (role === "student") return <RedirectTo to="/mypage/home" />;
  return <MyPage />;
}

function StudentPageRoute({
  component: Component,
}: {
  component: () => JSX.Element;
}) {
  const { role, isLoading } = useCurrentStaffRole();
  if (isLoading) return <WorkspaceLoading />;
  if (role === "educator") return <RedirectTo to="/teacher" />;
  return <Component />;
}

function GuestLockedStudentHome() {
  return (
    <GuestDirectBriefingGate>
      <StudentHome />
    </GuestDirectBriefingGate>
  );
}

function GuestLockedStudentLearning() {
  return (
    <GuestDirectBriefingGate>
      <StudentLearning />
    </GuestDirectBriefingGate>
  );
}

function StudentHomeRoute() {
  return <StudentPageRoute component={GuestLockedStudentHome} />;
}

function StudentLearningRoute() {
  return <StudentPageRoute component={GuestLockedStudentLearning} />;
}

function StudentScoresRoute() {
  return <StudentPageRoute component={StudentScores} />;
}

function StudentSettingsRoute() {
  return <StudentPageRoute component={StudentSettings} />;
}

function StudentProfileRoute() {
  return <StudentPageRoute component={StudentProfile} />;
}

/** 教員画面の公開デモの評価画面。上のヘッダーからは教員画面のデモにだけ戻れる */
function DemoTeacherEvaluationRoute() {
  const [, navigate] = useLocation();
  const backToDemo = () => navigate(DEMO_TEACHER_BASE);
  return (
    <div className="bg-medical-light" style={{ minHeight: "100dvh" }}>
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <StudentHeader
          onLogoClick={backToDemo}
          logoLabel="教員ページ（デモ）へ戻る"
          showStudentIcons={false}
          actions={
            <NavButton icon={<IconHome />} label="教員ページ（デモ）へ" variant="outline" onClick={backToDemo} />
          }
        />
      </div>
      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        style={{ paddingTop: STUDENT_HEADER_HEIGHT + 32 }}
      >
        <Evaluation />
      </main>
    </div>
  );
}

/** 教員画面の公開デモ（/demo/teacher）。本物の教員画面と同じ並びで、デモの中だけを行き来する */
function renderDemoTeacherRoute(location: string): JSX.Element | null {
  if (location !== DEMO_TEACHER_BASE && !location.startsWith(`${DEMO_TEACHER_BASE}/`)) return null;
  const path = location.split("?")[0].replace(/\/+$/, "");
  if (path === DEMO_TEACHER_BASE) return <DemoTeacherDashboard />;
  if (path === `${DEMO_TEACHER_BASE}/rankings`) return <DemoTeacherRanking />;
  if (path === `${DEMO_TEACHER_BASE}/students`) return <DemoTeacherStudents />;
  if (path === `${DEMO_TEACHER_BASE}/scores`) return <DemoTeacherScores />;
  if (path === `${DEMO_TEACHER_BASE}/evaluation`) return <DemoTeacherEvaluationRoute />;
  if (/^\/demo\/teacher\/classes\/\d+$/.test(path)) return <DemoTeacherClassDashboard />;
  const studentMatch = path.match(/^\/demo\/teacher\/students\/([^/]+)$/);
  if (studentMatch) return <DemoTeacherStudentScores participantCode={decodeURIComponent(studentMatch[1])} />;
  return <RedirectTo to={DEMO_TEACHER_BASE} />;
}

function Router() {
  const [location] = useLocation();

  const demoTeacherRoute = renderDemoTeacherRoute(location);
  if (demoTeacherRoute) return demoTeacherRoute;

  const publicStudentScoreMatch = location.match(/^\/demo\/rankings\/([^/]+)$/);
  if (publicStudentScoreMatch) {
    return (
      <PublicStudentScoreSample
        participantCode={decodeURIComponent(publicStudentScoreMatch[1])}
      />
    );
  }

  if (location === "/demo/rankings") {
    return <PublicRankingSample />;
  }

  if (location === "/mypage/mock") {
    return <StudentHomeMock />;
  }

  const isAdminPage =
    location.startsWith("/admin") ||
    location.startsWith("/teacher") ||
    location.startsWith("/mypage");

  if (isAdminPage) {
    return (
      <>
        <AdminRouter />
      </>
    );
  }

  return (
    <div className="bg-medical-light" style={{ minHeight: "100dvh" }}>
      <StudentAppHeader />
      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        style={{ paddingTop: STUDENT_HEADER_HEIGHT + 32 }}
      >
        <MainRouter />
      </main>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router />
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
